import { create } from 'zustand';

interface ConfirmState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  type: 'danger' | 'warning' | 'info' | 'success';
  onConfirm: (() => void) | null;
  onCancel: (() => void) | null;
}

interface ConfirmStore extends ConfirmState {
  openConfirm: (options: Partial<ConfirmState>) => Promise<boolean>;
  closeConfirm: () => void;
  confirm: () => void;
  cancel: () => void;
}

export const useConfirmStore = create<ConfirmStore>((set, get) => ({
  isOpen: false,
  title: 'Confirm',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  type: 'warning',
  onConfirm: null,
  onCancel: null,

  openConfirm: (options) => {
    return new Promise<boolean>((resolve) => {
      set({
        ...options,
        isOpen: true,
        onConfirm: () => {
          get().closeConfirm();
          resolve(true);
        },
        onCancel: () => {
          get().closeConfirm();
          resolve(false);
        },
      });
    });
  },

  closeConfirm: () => {
    set({
      isOpen: false,
      title: 'Confirm',
      message: '',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      type: 'warning',
      onConfirm: null,
      onCancel: null,
    });
  },

  confirm: () => {
    const { onConfirm } = get();
    if (onConfirm) onConfirm();
  },

  cancel: () => {
    const { onCancel } = get();
    if (onCancel) onCancel();
  },
}));

// Convenience hook for using confirm dialog
export const useConfirm = () => {
  const { openConfirm } = useConfirmStore();

  const confirm = async (
    message: string,
    title: string = 'Confirm',
    options?: {
      confirmText?: string;
      cancelText?: string;
      type?: 'danger' | 'warning' | 'info' | 'success';
    }
  ): Promise<boolean> => {
    return openConfirm({
      title,
      message,
      confirmText: options?.confirmText || 'Confirm',
      cancelText: options?.cancelText || 'Cancel',
      type: options?.type || 'warning',
    });
  };

  return { confirm };
};
